import { defineStore } from 'pinia';

/**
 * Modal Store
 * Manages the state and visibility of all modals throughout the application
 */
export const useModalStore = defineStore('modal', {
    state: () => ({
        modals: {
            user_management: {
                remove: {
                    email: '',
                    show: false,
                    employeeId: '',
                },
            },
            examples: {
                generic: {
                    show: false,
                    title: '',
                    subheading: '',
                    samples: [],
                    tracking: null,
                },
            },
            request: {
                apply: {
                    requestId: '',
                    educatorName: '',
                    show: false,
                },
                reschedule: {
                    show: false,
                },
                edit: {
                    show: false,
                },
                cancel: {
                    show: false,
                    requestStatus: '',
                },
                approve: {
                    show: false,
                },
                decline: {
                    show: false,
                },
                'decline-direct': {
                    show: false,
                    requestId: '',
                },
            },
            requestTemplate: {
                create: {
                    show: false,
                },
                edit: {
                    show: false,
                    template: {},
                },
            },
            settings: {
                upload_image: {
                    show: false,
                },
            },
            error: {
                download_failed: {
                    show: false,
                    message: '',
                },
            },
        },
    }),

    getters: {
        /**
         * Examples generic modal state
         * @param {Object} state - Store state
         * @returns {Object} Examples generic modal object with show property
         */
        examplesModal(state) {
            return state.modals.examples.generic;
        },

        /**
         * Avatar upload modal state
         * @param {Object} state - Store state
         * @returns {Object} Avatar upload modal object with show property
         */
        avatarUploadModal(state) {
            return state.modals.settings.upload_image;
        },

        /**
         * Remove user modal state
         * @param {Object} state - Store state
         * @returns {Object} Remove user modal object with email, show, and employeeId properties
         */
        removeUserModal(state) {
            return state.modals.user_management.remove;
        },

        /**
         * Request apply modal state
         * @param {Object} state - Store state
         * @returns {Object} Request apply modal object with requestId, educatorName, and show properties
         */
        requestApplyModal(state) {
            return state.modals.request.apply;
        },

        /**
         * Request cancel modal state
         * @param {Object} state - Store state
         * @returns {Object} Request cancel modal object with show and requestStatus properties
         */
        requestCancelModal(state) {
            return state.modals.request.cancel;
        },

        /**
         * Request approve modal state
         * @param {Object} state - Store state
         * @returns {Object} Request approve modal object with show property
         */
        requestApproveModal(state) {
            return state.modals.request.approve;
        },

        /**
         * Request decline modal state
         * @param {Object} state - Store state
         * @returns {Object} Request decline modal object with show property
         */
        requestDeclineModal(state) {
            return state.modals.request.decline;
        },

        /**
         * Request decline direct modal state
         * @param {Object} state - Store state
         * @returns {Object} Request decline direct modal object with show and requestId properties
         */
        requestDeclineDirectModal(state) {
            return state.modals.request['decline-direct'];
        },

        /**
         * Request reschedule modal state
         * @param {Object} state - Store state
         * @returns {Object} Request reschedule modal object with show property
         */
        requestRescheduleModal(state) {
            return state.modals.request.reschedule;
        },

        /**
         * Request edit modal state
         * @param {Object} state - Store state
         * @returns {Object} Request edit modal object with show property
         */
        requestEditModal(state) {
            return state.modals.request.edit;
        },

        /**
         * Employee ID to be removed
         * @param {Object} state - Store state
         * @returns {string} Employee ID from remove user modal
         */
        employeeToRemove(state) {
            return state.modals.user_management.remove.employeeId;
        },

        /**
         * Create request template modal state
         * @param {Object} state - Store state
         * @returns {Object} Create request template modal object with show property
         */
        createRequestTemplateModal(state) {
            return state.modals.requestTemplate.create;
        },

        /**
         * Edit request template modal state
         * @param {Object} state - Store state
         * @returns {Object} Edit request template modal object with show and template properties
         */
        editRequestTemplateModal(state) {
            return state.modals.requestTemplate.edit;
        },
    },

    actions: {
        /**
         * Toggle modal visibility and update modal state
         * @param {Object} data - Modal data object
         * @param {string} data.category - Modal category (e.g., 'request', 'examples', 'user_management')
         * @param {string} data.type - Modal type within the category (e.g., 'apply', 'cancel', 'generic')
         * @param {boolean} data.show - Whether to show or hide the modal
         * @param {...*} rest - Additional properties to set on the modal state
         */
        toggleModal(data) {
            const { category, type, show, ...rest } = data;

            this.modals[category][type].show = show;

            Object.keys(rest).forEach((key) => {
                this.modals[category][type][key] = data[key];
            });
        },

        /**
         * Resets the state of the examples modal.
         * @param {boolean} show - Whether to show or hide the modal
         */
        resetExamplesModal(show = false) {
            this.modals.examples.generic = {
                show,
                title: '',
                subheading: '',
                samples: [],
                tracking: null,
            };
        },
    },
});
